import React, { useEffect, useMemo, useState } from "react";
import "./CellHistoryDialog.css";

/**
 * AllChangesDialog — Excel-style "Show Changes" for the whole workbook.
 *
 * Lists every recorded edit (all sheets, all cells), newest first, with
 * a filter by editor email. Data comes from the same ExcelWebEdit history
 * endpoint used by the per-cell dialog.
 */
export default function AllChangesDialog({ open, fetchHistory, onClose }) {
  const [state, setState] = useState({ loading: true, error: "", entries: [] });
  const [emailFilter, setEmailFilter] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState({ loading: true, error: "", entries: [] });
    setEmailFilter("");
    fetchHistory()
      .then((all) => {
        if (cancelled) return;
        const entries = [...all].sort(
          (a, b) => new Date(b.editedAtUtc) - new Date(a.editedAtUtc)
        );
        setState({ loading: false, error: "", entries });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err.message || "Unable to load history", entries: [] });
      });
    return () => { cancelled = true; };
  }, [open, fetchHistory]);

  const emails = useMemo(
    () => Array.from(new Set(state.entries.map((e) => e.email))).sort(),
    [state.entries]
  );
  const visible = useMemo(
    () => (emailFilter ? state.entries.filter((e) => e.email === emailFilter) : state.entries),
    [state.entries, emailFilter]
  );

  if (!open) return null;

  return (
    <>
      <div className="sev-history-backdrop" onClick={onClose} />
      <div className="sev-history-dialog sev-history-wide" role="dialog" aria-label="All changes">
        <div className="sev-history-head">
          <span className="material-icons">history</span>
          <span className="sev-history-title">All changes</span>
          <button type="button" className="sev-history-close" onClick={onClose} title="Close">
            <span className="material-icons">close</span>
          </button>
        </div>

        {!state.loading && !state.error && state.entries.length > 0 && (
          <div className="sev-history-filter">
            <label htmlFor="sev-history-email">Edited by</label>
            <select
              id="sev-history-email"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
            >
              <option value="">All ({state.entries.length})</option>
              {emails.map((em) => (
                <option key={em} value={em}>
                  {em} ({state.entries.filter((x) => x.email === em).length})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="sev-history-body">
          {state.loading && <div className="sev-history-status">Loading history...</div>}
          {!state.loading && state.error && (
            <div className="sev-history-status sev-history-error">{state.error}</div>
          )}
          {!state.loading && !state.error && visible.length === 0 && (
            <div className="sev-history-status">No edits recorded for this workbook.</div>
          )}
          {!state.loading && !state.error && visible.map((e, i) => (
            <div className="sev-history-entry" key={i}>
              <div className="sev-history-meta">
                <span className="sev-history-cellref">{e.sheet}!{e.cellRef}</span>
                <span className="sev-history-who">{e.email}</span>
                <span className="sev-history-when">{new Date(e.editedAtUtc).toLocaleString()}</span>
              </div>
              <div className="sev-history-change">
                <span className="sev-history-old">{e.oldValue === "" ? "(empty)" : e.oldValue}</span>
                <span className="material-icons sev-history-arrow">arrow_forward</span>
                <span className="sev-history-new">{e.newValue === "" ? "(empty)" : e.newValue}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
